import type { LiveOption } from './config';

/**
 * Aggregate results, a projection of the event stream rather than a source of truth. The widget
 * treats them as advisory: if they are missing or malformed the reader still sees a thank you.
 */
export interface ResultsDoc {
  readonly version: number;
  readonly total_responses: number;
  readonly counts: Readonly<Record<string, number>>;
}

export interface Share {
  readonly id: string;
  readonly label: string;
  readonly count: number;
  /** 0 to 1, for the bar width. */
  readonly fraction: number;
  /** Rounded whole percent, for the label. */
  readonly percent: number;
}

export interface Tally {
  readonly total: number;
  readonly shares: readonly Share[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function count(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return 0;
  return Math.floor(value);
}

export function parseResults(raw: unknown): ResultsDoc | null {
  if (!isRecord(raw)) return null;

  const rawCounts = raw['counts'];
  if (!isRecord(rawCounts)) return null;

  const counts: Record<string, number> = {};
  for (const key of Object.keys(rawCounts)) counts[key] = count(rawCounts[key]);

  const version = raw['version'];

  return {
    version: typeof version === 'number' && Number.isFinite(version) ? version : 1,
    total_responses: count(raw['total_responses']),
    counts,
  };
}

/**
 * Turn counts into shares.
 *
 * `includeOwnVote` exists because a vote reaches the aggregate through the event stream and the
 * projector, which takes a moment. Showing a reader zero percent for the option they just chose
 * reads as broken, so their own vote is added locally for the render that immediately follows it.
 * A returning reader, whose vote landed long ago, is shown the server's numbers untouched.
 *
 * The denominator prefers `total_responses` but falls back to the sum of the counts, so a
 * projection that has drifted still produces shares that add up rather than a bar over 100 percent.
 */
export function computeShares(
  options: readonly LiveOption[],
  doc: ResultsDoc | null,
  ownOptionId: string | null,
  includeOwnVote: boolean,
): Tally {
  const counts = doc?.counts ?? {};
  const own = includeOwnVote && ownOptionId !== null ? ownOptionId : null;

  const raw = options.map((option) => {
    const base = count(counts[option.id]);
    return { option, count: option.id === own ? base + 1 : base };
  });

  const summed = raw.reduce((sum, entry) => sum + entry.count, 0);
  const reported = (doc?.total_responses ?? 0) + (own !== null ? 1 : 0);
  const total = Math.max(reported, summed);

  const shares = raw.map(({ option, count: value }) => {
    const fraction = total > 0 ? value / total : 0;
    return {
      id: option.id,
      label: option.label,
      count: value,
      fraction,
      percent: Math.round(fraction * 100),
    };
  });

  return { total, shares };
}
